import type React from "react";
import {useEffect, useState} from "react";

import {timeSince} from "~/util/timeUtils";

// Assume that the timeSince function is exported from your utility file.

interface TimeSinceProps {
  date?: Date;
}

const TimeSince: React.FC<TimeSinceProps> = ({date}) => {
  // If date is not provided, display a fallback string.
  const initialText = date ? timeSince(date) : "N/A";
  const [display, setDisplay] = useState<string>(initialText);

  useEffect(() => {
    // If no date is provided, we don't need to schedule any updates.
    if (!date) {
      return;
    }

    let timerId: ReturnType<typeof setTimeout>;

    // Calculate the delay until the next update.
    const computeDelay = (): number => {
      const now = new Date();
      const secondsElapsed = (now.getTime() - date.getTime()) / 1000;

      if (secondsElapsed < 60) {
        return 1000; // update every second
      }
      if (secondsElapsed < 3600) {
        const secPastMinute = secondsElapsed % 60;
        return (60 - secPastMinute) * 1000;
      }
      if (secondsElapsed < 86400) {
        const secPastHour = secondsElapsed % 3600;
        return (3600 - secPastHour) * 1000;
      }
      if (secondsElapsed < 31536000) {
        const secPastDay = secondsElapsed % 86400;
        return (86400 - secPastDay) * 1000;
      }
      const secPastMonth = secondsElapsed % 2592000; // Approximate month (30 days)
      return (2592000 - secPastMonth) * 1000;
    };

    // Function to update the display and set the next timeout.
    const tick = () => {
      setDisplay(timeSince(date));
      timerId = setTimeout(tick, computeDelay());
    };

    timerId = setTimeout(tick, computeDelay());

    // Clean up any existing timers if the date changes or the component unmounts.
    return () => clearTimeout(timerId);
  }, [date]);

  return <>{display}</>;
};

export default TimeSince;
